/**
 * Auxiliary stores for the command parity waves: block definitions, named
 * views / workspace flags, analysis display modes, session clipboard.
 * (docs/architecture/command-parity-plan.md)
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { settingsStorage } from '../utils/settingsPersistence';
import type { Entity } from './store';
import type { BIMElement } from '../core/types';
import type { SketchSolid, SketchLooseEdge } from '../core/sketch/types';

// ----------------------------- blocks -----------------------------
export interface BlockAttribute {
    tag: string;
    prompt: string;
    defaultValue: string;
}

export interface BlockDef {
    name: string;
    entities: Entity[];
    basePoint: [number, number, number];
    attributes: BlockAttribute[];
    /** Dynamic-block visibility states (BVSTATE). */
    visibilityStates?: string[];
    /** Parameters/actions metadata (authoring parity, simplified). */
    parameters?: { id: string; kind: string }[];
    actions?: { id: string; kind: string; paramId: string }[];
}

interface BlockState {
    defs: Record<string, BlockDef>;
    define: (def: BlockDef) => void;
    remove: (name: string) => void;
    setAttributes: (name: string, attrs: BlockAttribute[]) => void;
    addVisState: (name: string, state: string) => void;
    tagVisState: (name: string, state: string, indices: number[]) => void;
    addParameter: (name: string, kind: string) => void;
    addAction: (name: string, kind: string, paramId: string) => void;
}

export const useBlockStore = create<BlockState>()((set) => ({
    defs: {},
    define: (def) => set((s) => ({ defs: { ...s.defs, [def.name]: def } })),
    remove: (name) => set((s) => {
        const defs = { ...s.defs };
        delete defs[name];
        return { defs };
    }),
    setAttributes: (name, attributes) => set((s) => (
        s.defs[name] ? { defs: { ...s.defs, [name]: { ...s.defs[name], attributes } } } : s
    )),
    addVisState: (name, state) => set((s) => {
        const def = s.defs[name];
        if (!def) return s;
        const list = [...(def.visibilityStates ?? []), state];
        return { defs: { ...s.defs, [name]: { ...def, visibilityStates: list } } };
    }),
    /** Authoring: tag definition entities (by index) with a visibility state. */
    tagVisState: (name: string, state: string, indices: number[]) => set((s) => {
        const def = s.defs[name];
        if (!def) return s;
        const entities = def.entities.map((e, i) => (
            indices.includes(i)
                ? { ...e, data: { ...e.data, visState: state } }
                : e
        ));
        const list = def.visibilityStates?.includes(state)
            ? def.visibilityStates
            : [...(def.visibilityStates ?? []), state];
        return { defs: { ...s.defs, [name]: { ...def, entities, visibilityStates: list } } };
    }),
    addParameter: (name, kind) => set((s) => {
        const def = s.defs[name];
        if (!def) return s;
        const id = `P${(def.parameters?.length ?? 0) + 1}`;
        return { defs: { ...s.defs, [name]: { ...def, parameters: [...(def.parameters ?? []), { id, kind }] } } };
    }),
    addAction: (name, kind, paramId) => set((s) => {
        const def = s.defs[name];
        if (!def) return s;
        const id = `A${(def.actions?.length ?? 0) + 1}`;
        return { defs: { ...s.defs, [name]: { ...def, actions: [...(def.actions ?? []), { id, kind, paramId }] } } };
    }),
}));

// ----------------------------- views / workspace -----------------------------
export interface NamedView {
    name: string;
    position: [number, number, number];
    target: [number, number, number];
    zoom?: number;
}

export type AnalysisMode = 'none' | 'edges' | 'normals' | 'zebra' | 'draft' | 'curvature' | 'envmap' | 'section';

interface ViewState {
    namedViews: NamedView[];
    /** Turntable presentation spin. */
    turntable: boolean;
    /** Polar tracking + smart track + isodraft plane. */
    polarOn: boolean;
    smartTrack: boolean;
    isoplane: 'off' | 'left' | 'top' | 'right';
    /** Analysis overlay mode (I wave). */
    analysisMode: AnalysisMode;
    /** CURVATUREGRAPH result for the selected curve (runner-written,
     *  AnalysisOverlay-rendered): world points + per-interior-vertex
     *  curvature (kappas[i] belongs to points[i+1]). */
    curvatureGraph: { points: [number, number, number][]; kappas: number[] } | null;
    /** Live section plane (J/section): a,b define vertical plane, off = none. */
    section: { a: [number, number]; b: [number, number] } | null;
    /** UI visibility toggles (RIBBON/MENUBAR/TOOLPALETTE parity flags). */
    ribbonVisible: boolean;
    /** HISTORY command flag: entity edits keep a recompute history. */
    recordHistory: boolean;

    saveView: (v: NamedView) => void;
    restoreView: (name: string) => NamedView | undefined;
    deleteView: (name: string) => void;
    /** Replace the whole saved-view list (document load rehydration). */
    replaceAllViews: (views: NamedView[]) => void;
    set: <K extends keyof Omit<ViewState, 'namedViews' | 'saveView' | 'restoreView' | 'deleteView' | 'replaceAllViews'>>(
        key: K, value: ViewState[K]
    ) => void;
}

export const useViewStore = create<ViewState>()((set, get) => ({
    namedViews: [],
    turntable: false,
    polarOn: false,
    smartTrack: false,
    isoplane: 'off',
    analysisMode: 'none',
    curvatureGraph: null,
    section: null,
    ribbonVisible: true,
    recordHistory: true,
    saveView: (v) => set((s) => ({
        namedViews: [...s.namedViews.filter((x) => x.name !== v.name), v],
    })),
    restoreView: (name) => get().namedViews.find((v) => v.name === name),
    deleteView: (name) => set((s) => ({ namedViews: s.namedViews.filter((v) => v.name !== name) })),
    replaceAllViews: (views) => set({ namedViews: views }),
    set: (key, value) => set({ [key]: value } as Partial<ViewState>),
}));

// ----------------------------- clipboard -----------------------------
/**
 * One cross-workbench clipboard entry: the owning source plus its own
 * serializable citizen (B4 clipboard contract). Paste routes each entry
 * back through its owner (editRouter.pasteSceneClipboard), so a mixed
 * COPYBASE selection survives the trip and re-creates as the same kinds
 * of citizens at the same shared-world coordinates.
 */
export type SceneClipboardEntry =
    | { source: 'cad'; entity: Entity }
    | { source: 'bim'; element: BIMElement }
    | { source: 'sketch'; solid: SketchSolid }
    | { source: 'sketchEdge'; edge: SketchLooseEdge };

interface ClipboardState {
    /** CAD subset — legacy shape the PASTEBLOCK anonymous-block flow builds from. */
    entities: Entity[];
    /** Full cross-workbench clipboard (B4). */
    entries: SceneClipboardEntry[];
    basePoint: [number, number, number] | null;
    copy: (list: Entity[], base: [number, number, number]) => void;
    copyCitizens: (entries: SceneClipboardEntry[], base: [number, number, number]) => void;
    pasteAt: () => Entity[];
    clear: () => void;
}

export const useClipboardStore = create<ClipboardState>((set, get) => ({
    entities: [],
    entries: [],
    basePoint: null,
    copy: (list, base) => set({
        entities: structuredClone(list),
        entries: list.map((entity) => ({ source: 'cad', entity: structuredClone(entity) }) as SceneClipboardEntry),
        basePoint: base,
    }),
    copyCitizens: (entries, base) => set({
        // Keep the CAD subset in `entities` so PASTEBLOCK's anonymous-block
        // flow (CAD-only by definition) keeps working unchanged.
        entities: structuredClone(
            entries.filter((e): e is Extract<SceneClipboardEntry, { source: 'cad' }> => e.source === 'cad')
                .map((e) => e.entity)
        ),
        entries: structuredClone(entries),
        basePoint: base,
    }),
    pasteAt: () => {
        const { entities } = get();
        // Fresh ids on every paste.
        return entities.map((e) => ({
            ...e,
            id: `cad:${crypto.randomUUID()}`,
            data: structuredClone(e.data),
            name: `${e.name} (paste)`,
        }));
    },
    clear: () => set({ entities: [], entries: [], basePoint: null }),
}));

// ----------------------------- macro session -----------------------------
interface MacroState {
    recording: boolean;
    steps: string[];
    start: () => void;
    log: (line: string) => void;
    stopAndName: (name: string) => string[];
}

export const useMacroStore = create<MacroState>((set, get) => ({
    recording: false,
    steps: [],
    start: () => set({ recording: true, steps: [] }),
    log: (line) => {
        if (get().recording) set((s) => ({ steps: [...s.steps, line] }));
    },
    stopAndName: () => {
        set({ recording: false });
        return get().steps;
    },
}));

// ----------------------------- layer states -----------------------------
/** One layer's on/off + freeze/lock + current flag inside a saved state. */
export interface LayerStateRecord {
    id: string;
    visible: boolean;
    frozen: boolean;
    locked: boolean;
    current: boolean;
}

interface LayerStateStore {
    /** Named snapshots (LAYERSTATE Manager parity), persisted with settings. */
    states: Record<string, LayerStateRecord[]>;
    save: (name: string, records: LayerStateRecord[]) => void;
    remove: (name: string) => void;
}

export const useLayerStateStore = create<LayerStateStore>()(
    persist(
        (set) => ({
            states: {},
            save: (name, records) => set((s) => ({ states: { ...s.states, [name]: records } })),
            remove: (name) => set((s) => {
                const next = { ...s.states };
                delete next[name];
                return { states: next };
            }),
        }),
        { name: 'yap-layer-states', storage: settingsStorage, partialize: (s) => ({ states: s.states }) }
    )
);

// ----------------------------- style stores (mline / leader) -----------------------------
/** MLINESTYLE: element offsets (as fractions of total width) + fill flag. */
export interface MlineStyle {
    id: string;
    name: string;
    /** number of parallel elements (2-5). */
    elements: number;
    width: number;
    fill: boolean;
}

interface MlineStyleStore {
    styles: Record<string, MlineStyle>;
    activeId: string;
    save: (style: MlineStyle) => void;
    setActive: (id: string) => void;
}

export const useMlineStyleStore = create<MlineStyleStore>()(
    persist(
        (set) => ({
            styles: {
                standard: { id: 'standard', name: 'Standard', elements: 2, width: 0.2, fill: false },
            },
            activeId: 'standard',
            save: (style) => set((s) => ({ styles: { ...s.styles, [style.id]: style }, activeId: style.id })),
            setActive: (id) => set({ activeId: id }),
        }),
        { name: 'yap-mline-styles', storage: settingsStorage, partialize: (s) => ({ styles: s.styles, activeId: s.activeId }) }
    )
);

/** MLEADERSTYLE: content text size/arrow scale + color, consumed by leaders. */
export interface LeaderStyle {
    id: string;
    name: string;
    arrowScale: number;
    textScale: number;
    color: string;
}

interface LeaderStyleStore {
    styles: Record<string, LeaderStyle>;
    activeId: string;
    save: (style: LeaderStyle) => void;
    setActive: (id: string) => void;
}

export const useLeaderStyleStore = create<LeaderStyleStore>()(
    persist(
        (set) => ({
            styles: {
                standard: { id: 'standard', name: 'Standard', arrowScale: 1, textScale: 1, color: '#ffffff' },
            },
            activeId: 'standard',
            save: (style) => set((s) => ({ styles: { ...s.styles, [style.id]: style }, activeId: style.id })),
            setActive: (id) => set({ activeId: id }),
        }),
        { name: 'yap-leader-styles', storage: settingsStorage, partialize: (s) => ({ styles: s.styles, activeId: s.activeId }) }
    )
);
